import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Plus, Edit, Trash2, Play, Users, Video, FileText, Clock, Check, X, Shield } from 'lucide-react';
import api from '../utils/api';
import toast from 'react-hot-toast';

const TABS = [
  { key: 'courses', label: '课程管理' },
  { key: 'applications', label: '申请管理' },
  { key: 'users', label: '学员管理' },
];

const InstructorDashboard = () => {
  const [searchParams] = useSearchParams();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'courses');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newCourse, setNewCourse] = useState({
    title: '',
    description: '',
    category: '账号篇',
    accessType: 'public',
    price: { individual: 999, group: 999 },
    defaultAccessDays: 30
  });

  // 申请管理相关状态
  const [applications, setApplications] = useState([]);
  const [appFilter, setAppFilter] = useState('pending');
  const [appLoading, setAppLoading] = useState(false);
  const [approveDays, setApproveDays] = useState({});
  const [rejectNote, setRejectNote] = useState({});
  const [showRejectInput, setShowRejectInput] = useState(null);
  // 续期相关状态
  const [extendModal, setExtendModal] = useState(null);

  useEffect(() => {
    fetchCourses();
  }, []);

  useEffect(() => {
    if (activeTab === 'applications') {
      fetchApplications();
    }
  }, [activeTab, appFilter]);

  const fetchCourses = async () => {
    try {
      const data = await api.get('/courses/instructor/my-courses');
      if (data.success) {
        setCourses(data.data);
      }
    } catch (error) {
      toast.error('获取课程失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchApplications = async () => {
    setAppLoading(true);
    try {
      const data = await api.get(`/authorizations/instructor/applications?status=${appFilter}`);
      if (data.success) {
        setApplications(data.data);
      }
    } catch (error) {
      toast.error('获取申请列表失败');
    } finally {
      setAppLoading(false);
    }
  };

  const handleCreateCourse = async (e) => {
    e.preventDefault();
    try {
      const body = { ...newCourse };
      if (body.accessType === 'public') {
        delete body.defaultAccessDays;
      }
      const data = await api.post('/courses', body);
      if (data.success) {
        toast.success('课程创建成功');
        setShowAddModal(false);
        fetchCourses();
      }
    } catch (error) {
      toast.error(error.message || '创建失败');
    }
  };

  const handleDeleteCourse = async (courseId) => {
    if (!window.confirm('确定要删除这个课程吗？此操作不可恢复。')) return;
    try {
      const data = await api.delete(`/courses/${courseId}`);
      if (data.success) {
        toast.success('课程已删除');
        fetchCourses();
      }
    } catch (error) {
      toast.error('删除失败');
    }
  };

  const handleApprove = async (enrollmentId) => {
    const days = approveDays[enrollmentId] || 30;
    try {
      const data = await api.post(`/authorizations/enrollments/${enrollmentId}/approve`, { days });
      if (data.success) {
        toast.success(data.message || '已授权');
        fetchApplications();
      }
    } catch (error) {
      toast.error(error.message || '操作失败');
    }
  };

  const handleReject = async (enrollmentId) => {
    const note = rejectNote[enrollmentId] || '';
    try {
      const data = await api.post(`/authorizations/enrollments/${enrollmentId}/reject`, { note });
      if (data.success) {
        toast.success('已拒绝');
        setShowRejectInput(null);
        fetchApplications();
      }
    } catch (error) {
      toast.error(error.message || '操作失败');
    }
  };

  const handleExtend = async (enrollmentId) => {
    const app = applications.find(a => a._id === enrollmentId);
    setExtendModal({
      enrollmentId,
      studentName: app?.user?.username || '未知',
      courseTitle: app?.course?.title || '未知课程',
      currentExpiresAt: app?.expiresAt
    });
  };

  const closeExtendModal = () => setExtendModal(null);

  const handleExtendSubmit = async (days) => {
    if (!extendModal) return;
    try {
      const data = await api.post(`/authorizations/enrollments/${extendModal.enrollmentId}/extend`, { days });
      if (data.success) {
        toast.success(data.message || '续期成功');
        closeExtendModal();
        fetchApplications();
      }
    } catch (error) {
      toast.error(error.message || '操作失败');
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('zh-CN') : '';

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">讲师后台</h1>
        {activeTab === 'courses' && (
          <button onClick={() => setShowAddModal(true)} className="btn-primary flex items-center">
            <Plus className="h-4 w-4 mr-2" />新建课程
          </button>
        )}
      </div>

      {/* Tab 切换 */}
      <div className="flex border-b border-gray-200">
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* 课程管理 Tab */}
      {activeTab === 'courses' && (
        <>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="card p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">我的课程</p>
                  <p className="text-2xl font-bold">{courses.length}</p>
                </div>
                <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                  <Video className="h-6 w-6 text-primary-600" />
                </div>
              </div>
            </div>
            <div className="card p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">总学员</p>
                  <p className="text-2xl font-bold">
                    {courses.reduce((sum, c) => sum + (c.stats?.totalStudents || 0), 0)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <Users className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </div>
            <div className="card p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">授权课程</p>
                  <p className="text-2xl font-bold">
                    {courses.filter(c => c.accessType === 'authorized').length}
                  </p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <Shield className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </div>
          </div>

          <div className="card overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">课程</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">类型</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">状态</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">学员</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">价格</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {courses.map((course) => (
                  <tr key={course._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        {course.thumbnail ? (
                          <img src={course.thumbnail} alt="" className="h-10 w-10 rounded object-cover" />
                        ) : (
                          <div className="h-10 w-10 rounded bg-gray-200 flex items-center justify-center">
                            <Play className="h-5 w-5 text-gray-400" />
                          </div>
                        )}
                        <div className="ml-3">
                          <p className="font-medium">{course.title}</p>
                          <p className="text-sm text-gray-500">{course.category}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        course.accessType === 'authorized' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {course.accessType === 'authorized' ? '授权观看' : '公开'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        course.status === 'published' ? 'bg-green-100 text-green-800' :
                        course.status === 'draft' ? 'bg-gray-100 text-gray-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {course.status === 'published' ? '已发布' :
                         course.status === 'draft' ? '草稿' : '已归档'}
                      </span>
                    </td>
                    <td className="px-6 py-4">{course.stats?.totalStudents || 0}</td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <span className="text-red-600 font-medium">¥{course.price?.individual ?? 299}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <Link to={`/instructor/courses/${course._id}`} className="p-2 text-primary-600 hover:bg-primary-50 rounded">
                          <Edit className="h-4 w-4" />
                        </Link>
                        <button onClick={() => handleDeleteCourse(course._id)} className="p-2 text-red-600 hover:bg-red-50 rounded">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {courses.length === 0 && (
              <div className="text-center py-12 text-gray-500">暂无课程，点击上方按钮创建</div>
            )}
          </div>
        </>
      )}

      {/* 申请管理 Tab */}
      {activeTab === 'applications' && (
        <>
          {/* 状态筛选 */}
          <div className="flex gap-2">
            {[
              { key: 'pending', label: '待审核', color: 'bg-yellow-100 text-yellow-800' },
              { key: 'approved', label: '已通过', color: 'bg-green-100 text-green-800' },
              { key: 'rejected', label: '已拒绝', color: 'bg-red-100 text-red-800' },
              { key: 'expired', label: '已过期', color: 'bg-gray-100 text-gray-800' },
            ].map(f => (
              <button
                key={f.key}
                onClick={() => setAppFilter(f.key)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  appFilter === f.key ? f.color + ' ring-2 ring-offset-1' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          <div className="card overflow-hidden">
            {appLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              </div>
            ) : applications.length === 0 ? (
              <div className="text-center py-12 text-gray-500">暂无申请</div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">学员</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">课程</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">申请时间</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">状态</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">有效期</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {applications.map((app) => (
                    <tr key={app._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <p className="font-medium">{app.applicantName || app.user?.username || '未知'}</p>
                        <p className="text-xs text-gray-500">{app.applicantPhone || app.user?.phone || app.user?.email || ''}</p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm">{app.course?.title || '未知课程'}</p>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {formatDate(app.createdAt)}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          app.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          app.status === 'approved' ? 'bg-green-100 text-green-800' :
                          app.status === 'rejected' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {app.status === 'pending' ? '待审核' :
                           app.status === 'approved' ? '已通过' :
                           app.status === 'rejected' ? '已拒绝' : '已过期'}
                        </span>
                        {app.applicationNote && (
                          <p className="text-xs text-gray-400 mt-1">备注：{app.applicationNote}</p>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {app.expiresAt ? (
                          <span className={new Date(app.expiresAt) < new Date() ? 'text-red-500' : 'text-green-600'}>
                            {formatDate(app.expiresAt)}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end space-x-2">
                          {app.status === 'pending' && (
                            <>
                              <input
                                type="number"
                                placeholder="天数"
                                min="1"
                                defaultValue="30"
                                onChange={(e) => setApproveDays({ ...approveDays, [app._id]: parseInt(e.target.value) || 30 })}
                                className="w-16 px-2 py-1 border rounded text-sm text-center"
                              />
                              <button
                                onClick={() => handleApprove(app._id)}
                                className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                                title="通过"
                              >
                                <Check className="h-4 w-4" />
                              </button>
                              {showRejectInput === app._id ? (
                                <div className="flex items-center space-x-1">
                                  <input
                                    type="text"
                                    placeholder="拒绝原因"
                                    onChange={(e) => setRejectNote({ ...rejectNote, [app._id]: e.target.value })}
                                    className="w-24 px-2 py-1 border rounded text-xs"
                                    autoFocus
                                  />
                                  <button onClick={() => handleReject(app._id)} className="p-1 text-red-600 hover:bg-red-50 rounded text-xs">确认</button>
                                  <button onClick={() => setShowRejectInput(null)} className="p-1 text-gray-400 hover:bg-gray-50 rounded text-xs">取消</button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => setShowRejectInput(app._id)}
                                  className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                                  title="拒绝"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              )}
                            </>
                          )}
                          {app.status === 'approved' && (
                    <Link to="/instructor/users" className="px-2 py-1 text-xs bg-green-100 text-green-700 hover:bg-green-200 rounded transition-colors">
                      👥 管理学员
                    </Link>
                  )}
                          {app.status === 'rejected' && app.reviewNote && (
                            <span className="text-xs text-gray-400">原因：{app.reviewNote}</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* 续期弹窗 */}
      {extendModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-sm mx-4">
            <h2 className="text-xl font-bold mb-4">🔄 续期学习权限</h2>
            <div className="bg-gray-50 rounded-lg p-4 mb-4 text-sm">
              <p className="text-gray-600">学员：<span className="font-medium text-gray-900">{extendModal.studentName}</span></p>
              <p className="text-gray-600 mt-1">课程：<span className="font-medium text-gray-900">{extendModal.courseTitle}</span></p>
              <p className="text-gray-600 mt-1">
                当前到期：<span className={`font-medium ${new Date(extendModal.currentExpiresAt) < new Date() ? 'text-red-600' : 'text-green-600'}`}>
                  {new Date(extendModal.currentExpiresAt).toLocaleDateString('zh-CN')}
                </span>
              </p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">续期天数</label>
              <div className="grid grid-cols-4 gap-2 mb-3">
                {[30, 60, 90, 180].map(d => (
                  <button
                    key={d}
                    onClick={() => document.getElementById('extend-days-input').value = d}
                    className="py-2 text-sm border border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
                  >
                    {d}天
                  </button>
                ))}
              </div>
              <input
                id="extend-days-input"
                type="number"
                min="1"
                max="3650"
                defaultValue="90"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-lg"
              />
            </div>
            <div className="flex gap-3">
              <button onClick={closeExtendModal} className="flex-1 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors">
                取消
              </button>
              <button
                onClick={() => handleExtendSubmit(parseInt(document.getElementById('extend-days-input').value))}
                className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                ✅ 确认续期
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 添加课程弹窗 */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">新建课程</h2>
            <form onSubmit={handleCreateCourse} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">课程标题</label>
                <input
                  type="text" value={newCourse.title}
                  onChange={(e) => setNewCourse({ ...newCourse, title: e.target.value })}
                  className="input" required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">课程描述</label>
                <textarea
                  value={newCourse.description}
                  onChange={(e) => setNewCourse({ ...newCourse, description: e.target.value })}
                  className="input" rows={3} required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">分类</label>
                <select
                  value={newCourse.category}
                  onChange={(e) => setNewCourse({ ...newCourse, category: e.target.value })}
                  className="input"
                >
                  <option>全部</option>
                  <option>账号篇</option>
                  <option>短视频篇</option>
                  <option>直播篇</option>
                  <option>运营篇</option>
                  <option>职业技能</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">访问类型</label>
                <select
                  value={newCourse.accessType}
                  onChange={(e) => setNewCourse({ ...newCourse, accessType: e.target.value })}
                  className="input"
                >
                  <option value="public">公开（所有人可购买观看）</option>
                  <option value="authorized">授权观看（需申请审核）</option>
                </select>
              </div>
              {newCourse.accessType === 'authorized' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">默认授权天数</label>
                  <input
                    type="number" value={newCourse.defaultAccessDays}
                    onChange={(e) => setNewCourse({ ...newCourse, defaultAccessDays: parseInt(e.target.value) || 30 })}
                    className="input" min="1"
                  />
                  <p className="text-xs text-gray-400 mt-1">审核通过时默认授权时长，可手动调整</p>
                </div>
              )}
              {newCourse.accessType === 'public' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">单独购买价格</label>
                    <input
                      type="number" value={newCourse.price.individual}
                      onChange={(e) => setNewCourse({ ...newCourse, price: { ...newCourse.price, individual: parseInt(e.target.value) || 0 } })}
                      className="input" min="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">团购价格</label>
                    <input
                      type="number" value={newCourse.price.group}
                      onChange={(e) => setNewCourse({ ...newCourse, price: { ...newCourse.price, group: parseInt(e.target.value) || 0 } })}
                      className="input" min="0"
                    />
                  </div>
                </div>
              )}
              <div className="flex space-x-3 pt-4">
                <button type="button" onClick={() => setShowAddModal(false)} className="flex-1 btn-secondary">取消</button>
                <button type="submit" className="flex-1 btn-primary">创建</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default InstructorDashboard;
